import type {AiChatAnimation} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {AiChatCostType, showPurchaseCreditsDialog} from '@Libraries/ai-chat.library';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';

interface AnimationGenerationInitialResponse {
  key: string;
  pollingKey: string;
}

export enum AnimationGenerationErrorCode {
  INSUFFICIENT_CREDITS = 'insufficient_credits',
  BUSY = 'busy',
  SAFETY_CHILDREN = 'safety_children',
  SAFETY_SENSITIVE = 'safety_sensitive',
  SAFETY_COPYRIGHT = 'safety_copyright',
  ERROR = 'error',
}

export type AnimationGenerationError = Error & {code: AnimationGenerationErrorCode};

export function getAnimationErrorMessage(code: AnimationGenerationErrorCode | undefined): string {
  switch (code) {
    case AnimationGenerationErrorCode.BUSY:
      return window.i18next.t('pmwjs_animation_creation_error_busy');
    case AnimationGenerationErrorCode.SAFETY_CHILDREN:
      return window.i18next.t('pmwjs_animation_creation_error_children');
    case AnimationGenerationErrorCode.SAFETY_SENSITIVE:
      return window.i18next.t('pmwjs_animation_creation_error_sensitive');
    case AnimationGenerationErrorCode.SAFETY_COPYRIGHT:
      return window.i18next.t('pmwjs_animation_creation_error_copyright');
    default:
      return window.i18next.t('pmwjs_animation_creation_failed');
  }
}

export const ensureAnimationCredits = async (generateAudio: boolean): Promise<boolean> => {
  const cost = generateAudio ? AiChatCostType.ANIMATION_WITH_AUDIO_COST : AiChatCostType.ANIMATION_WITHOUT_AUDIO_COST;
  const {credits} = await refreshTeamCreditsInStore();
  if (credits < cost) {
    const purchaseData = await showPurchaseCreditsDialog();
    if (!purchaseData?.creditsV2 || purchaseData.creditsV2 < cost) {
      return false;
    }
  }
  return true;
};

export async function cancelAnimationGeneration(pollingKey: string): Promise<void> {
  await window.PMW.writeLocal('ai/cancelAnimationGeneration', {key: pollingKey});
}

export interface GenerateAnimationOptions {
  signal?: AbortSignal;
  onPollingKeyReceived?: (pollingKey: string) => void;
  prompt?: string;
  duration?: number;
  generateAudio?: boolean;
}

export async function generateAnimationForAiChatImage(imageHashedId: string, options: GenerateAnimationOptions = {}): Promise<AiChatAnimation> {
  const {signal, onPollingKeyReceived, prompt, duration, generateAudio} = options;

  const initiateGeneration = async (): Promise<AnimationGenerationInitialResponse> => {
    const response = (await window.PMW.writeLocal('ai/generateAnimationForAiChatImage', {
      imageHashedId,
      prompt: prompt ?? '',
      duration: duration ?? 6,
      generateAudio: generateAudio ?? false,
    })) as AnimationGenerationInitialResponse;

    onPollingKeyReceived?.(response.key);

    return response;
  };

  const checkStatus = async (response: AnimationGenerationInitialResponse, immediate = false): Promise<AiChatAnimation> => {
    if (signal?.aborted) {
      throw new DOMException('Animation generation cancelled by user', 'AbortError');
    }

    return (await window.PMW.readLocal('ai/checkAnimationGenerationStatus', {
      key: response.key,
      ...(immediate ? {streamImmediate: 1} : {}),
    })) as AiChatAnimation;
  };

  const result = (await window.PMW.pollingAjaxCallAsync(initiateGeneration, checkStatus, 300000)) as AiChatAnimation | undefined | string;

  if (signal?.aborted) {
    throw new DOMException('Animation generation cancelled by user', 'AbortError');
  }

  if (typeof result === 'string') {
    const err = new Error('Animation generation failed') as AnimationGenerationError;
    err.code = result as AnimationGenerationErrorCode;
    throw err;
  }

  if (!result) {
    const err = new Error('Animation generation timed out') as AnimationGenerationError;
    err.code = AnimationGenerationErrorCode.ERROR;
    throw err;
  }

  return result as AiChatAnimation;
}
